/**
 * Version History panel — lists immutable NetworkRelease snapshots for a network.
 *
 * Backed by GET /networks/{id}/releases. Read-only; selecting a row calls
 * ``onViewRelease(version)`` so the parent can open a detail drawer if desired.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { listNetworkReleases } from '@/services/networkReleasesApi';
import { formatRelativeTime } from '@/utils/formatDate';
import { displayVersionLabel } from '@stepflow/ui-shared/utils/stepflowVersionLabel';

export default function ReleaseVersionHistory({ networkId, onViewRelease }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [releases, setReleases] = useState([]);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!networkId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    listNetworkReleases(networkId, { limit: 50 })
      .then((res) => {
        if (cancelled) return;
        setReleases(Array.isArray(res.releases) ? res.releases : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load release history');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [networkId, reloadTick]);

  const reload = useCallback(() => setReloadTick((n) => n + 1), []);

  const sorted = useMemo(
    () => [...releases].sort((a, b) => (b.releaseVersion || 0) - (a.releaseVersion || 0)),
    [releases],
  );

  if (!networkId) return null;

  return (
    <div className="rounded-md border border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
        <h4 className="text-sm font-semibold text-gray-800">Version history</h4>
        <button
          type="button"
          onClick={reload}
          className="text-xs text-gray-500 hover:text-gray-800"
          disabled={loading}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="px-3 py-2 text-xs text-red-600" role="alert">
          {error}
        </div>
      )}

      {!loading && !error && sorted.length === 0 && (
        <div className="px-3 py-4 text-xs text-gray-500">
          No releases yet. Release this network to create the first immutable snapshot.
        </div>
      )}

      {sorted.length > 0 && (
        <ul className="divide-y divide-gray-100" role="list">
          {sorted.map((r) => (
            <li key={r.releaseVersion} className="flex items-center justify-between px-3 py-2">
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900">
                  {displayVersionLabel(r, sorted) || `Release ${r.releaseVersion}`}
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    {formatRelativeTime(r.releasedAt)}
                  </span>
                </div>
                <div className="truncate text-xs text-gray-500" title={r.releasedBy}>
                  Released by {r.releasedBy || 'unknown'}
                </div>
              </div>
              {typeof onViewRelease === 'function' && (
                <button
                  type="button"
                  onClick={() => onViewRelease(r.releaseVersion)}
                  className="shrink-0 rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                >
                  View snapshot
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
